import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowRight, Building2, HardDrive, KeyRound, LoaderCircle, Search as SearchIcon, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCustomerDirectoryQuery, useTenantSessionQuery } from "@/api/query-hooks";
import { ErrorState, LoadingState } from "@/components/layout/route-states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LocalCustomerCreateDialog } from "@/features/licensing/local-customer-create-dialog";

const pageSize = 24;

export function ClientDirectoryPage() {
  const { t } = useTranslation("licensing");
  const search = useSearch({ from: "/clients" });
  const navigate = useNavigate({ from: "/clients" });
  const queryText = search.q ?? "";
  const returnAnchor = search.anchor;
  const directoryMode = search.view === "all" ? "all" : "footprint";
  const [draft, setDraft] = useState(queryText);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const directory = useCustomerDirectoryQuery({ q: queryText, mode: directoryMode, limit: pageSize });
  const session = useTenantSessionQuery();
  const clients = useMemo(() => directory.data?.pages.flatMap((page) => page.items) ?? [], [directory.data]);

  useEffect(() => setDraft(queryText), [queryText]);
  useEffect(() => {
    const normalized = draft.trim();
    if (normalized === queryText) return;
    const timeout = window.setTimeout(() => {
      void navigate({ search: { q: normalized || undefined, anchor: undefined, view: search.view }, replace: true });
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [draft, navigate, queryText, search.view]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !directory.hasNextPage || directory.isFetchingNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void directory.fetchNextPage();
    }, { rootMargin: "320px 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [directory.fetchNextPage, directory.hasNextPage, directory.isFetchingNextPage]);

  useEffect(() => {
    if (!returnAnchor || directory.isLoading || directory.isError) return;
    const target = document.querySelector<HTMLElement>(`[data-client-id="${returnAnchor}"]`);
    if (target) {
      target.scrollIntoView({ block: "center" });
      target.focus({ preventScroll: true });
      void navigate({ search: { q: queryText || undefined, anchor: undefined, view: search.view }, replace: true });
      return;
    }
    if (directory.hasNextPage && !directory.isFetchingNextPage) void directory.fetchNextPage();
  }, [clients, directory, navigate, queryText, returnAnchor, search.view]);

  return (
    <div className="grid gap-5" data-testid="client-directory-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Building2 className="h-5 w-5" />{t("directory.title")}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t(directoryMode === "all" ? "directory.allDescription" : "directory.description")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!directory.isLoading && !directory.isError ? <Badge variant="default">{t("directory.loaded", { count: clients.length })}</Badge> : null}
          {session.data?.canManageCustomers ? <LocalCustomerCreateDialog onCreated={(clientId) => void navigate({ to: "/clients/$clientId", params: { clientId: String(clientId) }, search: { fromQ: undefined, fromClientId: undefined, fromView: "all" } })} /> : null}
        </div>
      </div>

      <section className="rounded-md border bg-panel p-3 sm:p-4">
        <div className="mb-4 grid gap-2" role="group" aria-label={t("directory.viewLabel")}>
          <span className="text-sm font-medium">{t("directory.viewLabel")}</span>
          <div className="grid grid-cols-2 gap-1 rounded-md bg-muted p-1 sm:flex sm:w-fit">
            <Button type="button" size="sm" variant={directoryMode === "footprint" ? "default" : "ghost"} aria-pressed={directoryMode === "footprint"} onClick={() => void navigate({ search: { q: queryText || undefined, anchor: undefined, view: undefined }, replace: true })}>
              {t("directory.footprintView")}
            </Button>
            <Button type="button" size="sm" variant={directoryMode === "all" ? "default" : "ghost"} aria-pressed={directoryMode === "all"} onClick={() => void navigate({ search: { q: queryText || undefined, anchor: undefined, view: "all" }, replace: true })}>
              {t("directory.allView")}
            </Button>
          </div>
        </div>
        <label className="text-sm font-medium" htmlFor="client-directory-search">{t("directory.searchLabel")}</label>
        <div className="relative mt-2">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input id="client-directory-search" className="h-11 pl-9 pr-11" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={t("directory.searchPlaceholder")} autoComplete="off" autoFocus />
          {draft ? (
            <Button type="button" size="icon" variant="ghost" className="absolute right-1 top-1/2 -translate-y-1/2" aria-label={t("directory.clearSearch")} onClick={() => {
              setDraft("");
              window.requestAnimationFrame(() => document.getElementById("client-directory-search")?.focus());
            }}><X className="h-4 w-4" /></Button>
          ) : null}
        </div>
        <div className="mt-2 min-h-5 text-xs text-muted-foreground" aria-live="polite">
          {directory.isFetching && !directory.isFetchingNextPage ? t("directory.searching") : queryText ? t("directory.filtered", { count: clients.length }) : t("directory.hint")}
        </div>
      </section>

      {directory.isLoading ? <LoadingState /> : null}
      {directory.isError ? <ErrorState title={t("directory.loadFailed")} detail={directory.error instanceof Error ? directory.error.message : t("directory.loadFailedDetail")} onRetry={() => void directory.refetch()} /> : null}

      {!directory.isLoading && !directory.isError && clients.length === 0 ? (
        <section className="rounded-md border bg-panel px-4 py-10 text-center">
          <Building2 className="mx-auto h-7 w-7 text-muted-foreground" />
          <h3 className="mt-3 font-medium">{queryText ? t("directory.noMatches") : t(directoryMode === "all" ? "directory.allEmpty" : "directory.empty")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{queryText ? t("directory.noMatchesHint") : t(directoryMode === "all" ? "directory.allEmptyHint" : "directory.emptyHint")}</p>
        </section>
      ) : null}

      {clients.length ? (
        <section aria-label={t(directoryMode === "all" ? "directory.allResultsLabel" : "directory.resultsLabel")} className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {clients.map((client) => (
            <Link key={client.id} to="/clients/$clientId" params={{ clientId: String(client.id) }} search={{ fromQ: queryText || undefined, fromClientId: String(client.id), fromView: search.view }} className="group flex min-w-0 flex-col rounded-md border bg-panel p-4 transition-colors hover:border-primary/40 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={t("directory.openClient", { name: client.name, clientId: client.number })} data-testid="client-directory-card" data-client-id={client.id}>
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted"><Building2 className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium leading-snug">{client.name}</span>
                  <span className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span>{t("directory.customerNumber", { number: client.number })}</span>
                    <Badge variant={client.status === "ACTIVE" ? "success" : "warning"}>{client.status}</Badge>
                  </span>
                </span>
              </div>
              <span className="mt-3 flex min-h-7 flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><HardDrive className="h-3.5 w-3.5" />{t("directory.targetsAll", { count: client.monitoringTargetCount })}</span>
                {client.hasLicensingFootprint ? <span className="inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" />{t("directory.hasLicensing")}</span> : null}
              </span>
              <div className="mt-4 flex items-center justify-end border-t pt-3">
                <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">{t("directory.open")}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
              </div>
            </Link>
          ))}
        </section>
      ) : null}

      {clients.length ? (
        <div ref={loadMoreRef} className="flex min-h-12 items-center justify-center text-sm text-muted-foreground" aria-live="polite" data-testid="client-directory-load-more">
          {directory.isFetchingNextPage ? <><LoaderCircle className="mr-2 h-4 w-4 animate-spin" />{t("directory.loadingMore")}</> : null}
          {!directory.hasNextPage && !directory.isFetchingNextPage ? t("directory.allLoaded", { count: clients.length }) : null}
        </div>
      ) : null}
    </div>
  );
}
